import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { getClassTypeName } from "../../constants/classTypes";
import {
  fetchCoverage,
  setSlotCoachCount,
  apiErrorMessage,
} from "../../services/api_coverage";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// "2026-10-06" -> "Tuesday", without the browser's timezone shifting the day
const weekdayOf = (isoDate) => {
  const [y, m, d] = isoDate.split("-").map(Number);
  return WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
};

// "17:15" -> { slot: "05:15 PM", label: "5:15 PM" }
const timeLabels = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  const mm = String(m).padStart(2, "0");
  return { slot: `${String(h12).padStart(2, "0")}:${mm} ${suffix}`, label: `${h12}:${mm} ${suffix}` };
};

// Recurring classes on one date with a 1 / 2 coach switch. The count belongs to
// the recurring slot on that weekday, so it applies to every week (the backend
// keeps it as `two_coach_days`). One-off classes always have one coach.
const CoachCountList = ({ date }) => {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(null);
  const weekday = weekdayOf(date);

  const { data, isLoading, isError } = useQuery(
    ["coverage", date, date],
    () => fetchCoverage({ startDate: date, endDate: date }),
    { enabled: !!date }
  );

  const classes = (data?.classes ?? []).filter((cls) => cls.recurring);

  const setCount = async (cls, coachCount) => {
    if (cls.capacity === coachCount) return;
    setSaving(cls.key);
    try {
      await setSlotCoachCount({
        time: timeLabels(cls.key.slice(11, 16)).slot,
        classType: cls.type || cls.class_type,
        days: [weekday],
        coachCount,
      });
      toast.success(
        `${timeLabels(cls.key.slice(11, 16)).label} now has ${coachCount === 2 ? "2 coaches" : "1 coach"} every ${weekday}.`
      );
    } catch (error) {
      toast.error(apiErrorMessage(error, "Couldn't change the coach count."));
    } finally {
      setSaving(null);
      // Coverage for every week changes, so refresh all of it
      queryClient.invalidateQueries("coverage");
    }
  };

  if (isLoading) return <p className="coach-count-note">Loading classes...</p>;
  if (isError) return <p className="coach-count-note">Couldn't load classes for this date.</p>;
  if (classes.length === 0) {
    return <p className="coach-count-note">No recurring classes on this date.</p>;
  }

  return (
    <div className="coach-count-list">
      <p className="coach-count-note">
        Changes apply to every {weekday}. Coaches already on a class keep their spot.
      </p>
      {classes.map((cls) => {
        const { label } = timeLabels(cls.key.slice(11, 16));
        return (
          <div key={cls.key} className="coach-count-row">
            <div className="coach-count-class">
              <span className="coach-count-time">{label}</span>
              <span className="coach-count-type">{getClassTypeName(cls.class_type)}</span>
            </div>
            <div className="coach-count-toggle" role="group" aria-label={`Coaches for ${label}`}>
              {[1, 2].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={cls.capacity === n ? "active" : ""}
                  aria-pressed={cls.capacity === n}
                  disabled={saving === cls.key}
                  onClick={() => setCount(cls, n)}
                >
                  {n === 1 ? "1 coach" : "2 coaches"}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CoachCountList;
